import { createContext, useContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { TokenValidationUnavailableError, validateToken } from '../config/api';
import { datadogRum } from '@datadog/browser-rum';
import { IepTimerMode } from '../types/accommodations';

interface User {
    email: string;
    userId: string;
    name?: string;
    currentTrack?: string;
    currentStage?: string;
    ageGrade?: number;
    focusTrack?: string;
    requiresG1Assessment?: boolean;
    role?: 'admin' | 'student'; // User role
    iepAccommodationsEnabled?: boolean;
    iepTimerMode?: IepTimerMode;
}

interface AuthContextType {
    user: User | null;
    loading: boolean;
    token: string | null;
    validationUnavailable: boolean;
    login: (token: string, userData?: Partial<User>) => void;
    logout: () => void;
    isAuthenticated: boolean;
    processToken: (token: string) => Promise<boolean>;
    retryValidation: () => Promise<void>;
    updateUserField: (fields: Partial<User>) => void;
}

interface DecodedAuthToken {
    email: string;
    userId: string;
    exp: number;
    role?: 'admin' | 'student';
}

const TOKEN_VALIDATION_RETRY_DELAYS_MS = [0, 250, 1000] as const;

const wait = (delayMs: number) => new Promise(resolve => setTimeout(resolve, delayMs));

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [validationUnavailable, setValidationUnavailable] = useState(false);

    const clearValidatedAuth = () => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        setValidationUnavailable(false);
        datadogRum.clearUser();
    };

    // Initialize auth only from localStorage
    useEffect(() => {
        const initializeAuth = async () => {
            try {
                const storedToken = localStorage.getItem('token');
                if (storedToken) {
                    await validateStoredToken(storedToken);
                }
            } catch (error) {
                console.error('Auth initialization error:', error);
                clearValidatedAuth();
            } finally {
                setLoading(false);
            }
        };

        initializeAuth();
        // Initialization must run once; retryValidation owns subsequent attempts.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // This is now only used for validating tokens from URL or localStorage
    const processToken = async (token: string) => {
        try {
            const decoded = jwtDecode<DecodedAuthToken>(token);
            if (!decoded.email || !decoded.userId || typeof decoded.exp !== 'number') {
                throw new Error('Invalid token format');
            }
            if (decoded.exp * 1000 < Date.now()) {
                return false;
            }

            const validation = await validateToken(token);

            if (!validation.valid || !validation.user) {
                return false;
            }
            const userData = {
                ...validation.user,
                email: decoded.email,
                userId: decoded.userId,
                role: decoded.role || validation.user.role || 'student' // Use role from JWT token first, then API, with fallback
            };
            const finalToken = validation.token || token;
            localStorage.setItem('token', finalToken);
            setToken(finalToken);
            setUser(userData);
            setValidationUnavailable(false);
            datadogRum.setUser({
                id: userData.userId,
                email: userData.email
            });
            return true;
        } catch (error) {
            if (error instanceof TokenValidationUnavailableError) {
                throw error;
            }

            console.error(`[Auth] Token validation failed with error: ${error instanceof Error ? error.message : 'Unknown error'}`);
            clearValidatedAuth();
            return false;
        }
    };

    const validateStoredToken = async (storedToken: string) => {
        for (let attempt = 0; attempt < TOKEN_VALIDATION_RETRY_DELAYS_MS.length; attempt += 1) {
            const delayMs = TOKEN_VALIDATION_RETRY_DELAYS_MS[attempt];
            if (delayMs > 0) {
                await wait(delayMs);
            }

            try {
                const isValid = await processToken(storedToken);
                if (!isValid) {
                    clearValidatedAuth();
                }
                return;
            } catch (error) {
                if (!(error instanceof TokenValidationUnavailableError)) {
                    throw error;
                }

                const isLastAttempt = attempt === TOKEN_VALIDATION_RETRY_DELAYS_MS.length - 1;
                if (!isLastAttempt) {
                    continue;
                }
            }
        }

        // Keep only the stored credential until the server can verify it.
        setToken(null);
        setUser(null);
        setValidationUnavailable(true);
        datadogRum.clearUser();
    };

    const retryValidation = async () => {
        const storedToken = localStorage.getItem('token');
        if (!storedToken) {
            clearValidatedAuth();
            return;
        }

        setLoading(true);
        try {
            await validateStoredToken(storedToken);
        } catch (error) {
            console.error('Auth retry error:', error);
            clearValidatedAuth();
        } finally {
            setLoading(false);
        }
    };

    // Simplified login that doesn't need validation
    const login = (newToken: string, userData?: Partial<User>) => {
        try {
            const decoded = jwtDecode<{ email: string; userId: string; role?: 'admin' | 'student' }>(newToken);
            if (!decoded.email || !decoded.userId) {
                throw new Error('Invalid token format');
            }
            const newUser = {
                email: decoded.email,
                userId: decoded.userId,
                role: decoded.role || userData?.role || 'student', // Use role from JWT token first, then userData, with fallback
                ...userData
            };
            localStorage.setItem('token', newToken);
            setToken(newToken);
            setUser(newUser);
            setValidationUnavailable(false);
            datadogRum.setUser({
                id: newUser.userId,
                email: newUser.email
            });
        } catch (error) {
            logout();
            throw error;
        }
    };

    const updateUserField = (fields: Partial<User>) => {
        setUser(prev => prev ? { ...prev, ...fields } : null);
    };

    const logout = () => {
        // Clear all localStorage items
        localStorage.clear();
        // Clear all sessionStorage items
        sessionStorage.clear();
        setToken(null);
        setUser(null);
        setValidationUnavailable(false);
        datadogRum.clearUser();
    };

    const contextValue = {
        user,
        loading,
        token,
        validationUnavailable,
        login,
        logout,
        isAuthenticated: !!user && !validationUnavailable,
        processToken,
        retryValidation,
        updateUserField,
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
